import {
  Drawer,
  Loading,
  Segmented,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { useUploadPaymentGroup } from '@/features/project/_hooks/uploader';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import {
  useCreatePaymentGroup,
  usePaymentGroups,
  useUpdatePaymentGroup,
} from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import Papa from 'papaparse';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { PaymentGroupForm } from './PaymentGroupForm';
import { PaymentGroupUploadForm } from './PaymentGroupUploadForm';

type SegmentType = 'single' | 'upload';

export const PaymentGroupFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { setPreviewRowState } = usePreviewRowState();
  const { setSelectedRowKeys, clearAllSelections } = useTableRowSelection();

  const [segment, setSegment] = useState<SegmentType>('single');
  const paymentGroups = usePaymentGroups({ projectId });
  const { mutateAsync: createPaymentGroupAsync } = useCreatePaymentGroup();
  const { mutateAsync: updatePaymentGroupAsync } = useUpdatePaymentGroup();
  const { mutateAsync: uploadPaymentGroup } = useUploadPaymentGroup();

  const { addExpandedRowKeys } = useExpandedRowKeysContext();

  const openDrawer = drawerState.open && drawerState.type === 'payment-group';

  if (paymentGroups.isPending || !paymentGroups.data) {
    return <Loading />;
  }

  const paymentGroup =
    openDrawer && drawerState.isEditing
      ? paymentGroups.data.find((pg) => pg.id === drawerState.paymentGroupId)
      : undefined;

  return (
    <Drawer
      open={openDrawer}
      onClose={() => {
        setDrawerState({ open: false });
        clearAllSelections();
      }}
      afterOpenChange={(visible) => {
        if (!visible) {
          // 新規作成でuploadを選択した後に編集しようとした時に、uploadを選択したままになってしまうので「single」に戻す
          setSegment('single');
        }
      }}
      destroyOnClose
    >
      {openDrawer && !drawerState.isEditing && (
        <Segmented
          value={segment}
          onChange={(value) => {
            setSegment(value as SegmentType);
          }}
          options={[
            { label: t('create-single-item'), value: 'single' },
            { label: t('csv-upload'), value: 'upload' },
          ]}
          style={{ marginBottom: 16 }}
        />
      )}
      {segment === 'single' && (
        <PaymentGroupForm
          {...(openDrawer && drawerState.isEditing
            ? {
                initialValues: {
                  ...paymentGroup,
                  overview: paymentGroup?.overview ?? undefined,
                  paymentCount: paymentGroup?.paymentCount ?? undefined,
                },
              }
            : {})}
          onSubmit={async ({ values, nextDrawerKey, onSuccess }) => {
            invariant(openDrawer, t('unreachable-code'));

            if (drawerState.isEditing) {
              await updatePaymentGroupAsync({
                id: drawerState.paymentGroupId,
                projectId,
                ...values,
              });
              await Promise.all([
                openMessage('success', t('message-update-completed')),
                (async () => {
                  setDrawerState({ open: false });
                })(),
              ]);
            } else {
              const res = await createPaymentGroupAsync({
                projectId,
                ...values,
              });
              await Promise.all([
                openMessage('success', t('message-create-completed')),
                (async () => {
                  if (!nextDrawerKey) {
                    setDrawerState({ open: false });
                  }
                  if (nextDrawerKey === 'payee-payment') {
                    setDrawerState({
                      open: true,
                      type: nextDrawerKey,
                      isEditing: false,
                      paymentGroupId: res.id,
                    });
                    setPreviewRowState({
                      type: nextDrawerKey,
                      paymentGroupId: res.id,
                    });
                    setSelectedRowKeys([PREVIEW_ROW_ID]);
                  }

                  addExpandedRowKeys([res.id]);
                })(),
                (async () => {
                  onSuccess?.();
                })(),
              ]);
            }
          }}
          usedDisplayCodes={paymentGroups.data.map(
            ({ displayCode }) => displayCode,
          )}
        />
      )}
      {segment === 'upload' && (
        <PaymentGroupUploadForm
          projectId={projectId}
          onSubmit={async (file: File) => {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onloadend = async () => {
              if (typeof reader.result !== 'string') {
                await openMessage(
                  'error',
                  t('error-message-invalid-reader-result'),
                );
                return;
              }

              const csvData = Papa.parse<Array<string | number>>(
                reader.result,
                {
                  skipEmptyLines: true,
                },
              );

              try {
                await uploadPaymentGroup({
                  projectId,
                  csvData: csvData.data,
                });

                await Promise.all([
                  openMessage(
                    'success',
                    t('message-bulk-upload-process-requested'),
                  ),
                  (async () => {
                    setDrawerState({ open: false });
                  })(),
                ]);
              } catch {
                await openMessage('error', t('message-update-failed'));
              }
            };
          }}
        />
      )}
    </Drawer>
  );
};
